import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tournament, TournamentClient, Participant, ParticipantClient, ParticipantDto, Person, PersonClient, BracketPreviewModel, ApproveBracketRequest, RoundApproveDto, MatchApproveDto } from '../../utils/apiClient';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import { useAuth } from '../Auth/AuthContext';
import './TournamentList.scss';
import { calculateAge } from '../utils';
import {
  formatAgeCategoryLabel,
  formatWeightCategoryLabel,
  resolveAgeCategory,
  resolveWeightCategory,
} from '../../utils/categoryResolver';
import BracketsPreview from './BracketsPreview';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';
import { getTournamentLifecycle, tournamentLifecycleLabel } from '../../utils/tournamentLifecycle';

// Using generated BracketPreviewModel type from apiClient

export const TournamentList = () => {
  const appContext = React.useContext(AppContext);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [registrationForm, setRegistrationForm] = useState<ParticipantDto>(new ParticipantDto({
    personId: 0,
    tournamentId: 0,
    weight: 0,
    organization: '',
    rank: ''
  }));
  const [selectedTournamentData, setSelectedTournamentData] = useState<Tournament | null>(null);
  const [currentUserData, setCurrentUserData] = useState<Person | null>(null);
  const [selectedWeightCategoryId, setSelectedWeightCategoryId] = useState<number | undefined>(undefined);

  // Preview state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTournament, setPreviewTournament] = useState<Tournament | null>(null);
  const [previewWeightCategoryId, setPreviewWeightCategoryId] = useState<number | undefined>(undefined);
  const [previewAgeCategoryId, setPreviewAgeCategoryId] = useState<number | undefined>(undefined);
  const [previewData, setPreviewData] = useState<BracketPreviewModel | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [connectorPaths, setConnectorPaths] = useState<string[]>([]);
  const showConnectors = false;

  const navigate = useNavigate();
  const tournamentClient = new TournamentClient(ApiUrl, axios);
  const participantClient = new ParticipantClient(ApiUrl, axios);
  const personClient = new PersonClient(ApiUrl, axios);
  const { user, isAdmin } = useAuth();

  useEffect(() => {
    fetchTournaments();
    if (user) {
      fetchUserParticipants();
      fetchCurrentUserData();
    }
  }, [user]);

  const fetchCurrentUserData = async () => {
    try {
      if (user?.id) {
        const userData = await personClient.getPerson(user.id);
        setCurrentUserData(userData);
      }
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    }
  };

  const fetchUserParticipants = async () => {
    try {
      const allParticipants = await participantClient.getParticipants();
      // Фильтруем участников текущего пользователя
      const userParticipants = allParticipants.filter(p => p.personId === user?.id);
      setParticipants(userParticipants);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    }
  };

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const data = await tournamentClient.getTournaments();
      setTournaments(data);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterClick = async (tournament: Tournament) => {
    try {
      // Получаем полные данные турнира для определения возрастных категорий
      const tournamentData = await tournamentClient.getTournament(tournament.id);
      setSelectedTournamentData(tournamentData);
      setSelectedTournament(tournament);
      setRegistrationForm(new ParticipantDto({
        personId: user?.id || 0,
        tournamentId: tournament.id!,
        weight: 0,
        organization: '',
        rank: ''
      }));
      setSelectedWeightCategoryId(undefined);
      setShowRegistrationModal(true);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    }
  };

  const handleRegistrationSubmit = async () => {
    try {
      await participantClient.createParticipant(registrationForm);
      await fetchUserParticipants(); // Обновляем список участников
      setShowRegistrationModal(false);
      setSelectedTournament(null);
    } catch (ex) {
      // eslint-disable-next-line
      console.log(ex);
      handleError(appContext!.setNotificationData, ex);
    }
  };

  const isUserRegistered = (tournamentId: number) => {
    return participants.some(p => p.tournamentId === tournamentId);
  };

  const getAgeCategoryText = (ageCategoryId?: number) => {
    if (!ageCategoryId || !selectedTournamentData) return "Не удалось определить категорию";
    const ageCategory = selectedTournamentData.ageCategories?.find(ac => ac.id === ageCategoryId);
    if (!ageCategory) return "Не удалось определить категорию";
    return formatAgeCategoryLabel(ageCategory, selectedTournamentData.ageCategories ?? []);
  };

  const getWeightCategoryText = (weightCategoryId?: number) => {
    if (!weightCategoryId || !selectedTournamentData) return "Не удалось определить категорию";
    const weightCategory = selectedTournamentData.weightCategories?.find(wc => wc.id === weightCategoryId);
    if (!weightCategory) return "Не удалось определить категорию";
    return formatWeightCategoryLabel(weightCategory, selectedTournamentData.weightCategories ?? []);
  };

  // Автоматическое определение возрастной категории при изменении веса
  useEffect(() => {
    if (selectedTournamentData && currentUserData && registrationForm.weight > 0) {
      const age = calculateAge(new Date(currentUserData.birthDate), new Date(selectedTournamentData.startDate));
      const matchingAgeCategory = resolveAgeCategory(age, selectedTournamentData.ageCategories ?? []);

      if (matchingAgeCategory) {
        setRegistrationForm(new ParticipantDto({
          ...registrationForm,
          ageCategoryInTournamentId: matchingAgeCategory.id
        }));
      } else {
        // Если не найдена подходящая категория, сбрасываем значение
        setRegistrationForm(new ParticipantDto({
          ...registrationForm,
          ageCategoryInTournamentId: undefined
        }));
      }
    }
  }, [registrationForm.weight, selectedTournamentData, currentUserData]);

  // Автоматическое определение весовой категории при изменении веса
  useEffect(() => {
    if (selectedTournamentData && registrationForm.weight > 0) {
      const matchingWeightCategory = resolveWeightCategory(
        registrationForm.weight,
        selectedTournamentData.weightCategories ?? []
      );

      if (matchingWeightCategory) {
        setSelectedWeightCategoryId(matchingWeightCategory.id);
      } else {
        // Если не найдена подходящая категория, сбрасываем значение
        setSelectedWeightCategoryId(undefined);
      }
    }
  }, [registrationForm.weight, selectedTournamentData]);

  // Автоматическое определение возрастной категории при открытии модального окна
  useEffect(() => {
    if (showRegistrationModal && selectedTournamentData && currentUserData) {
      const age = calculateAge(new Date(currentUserData.birthDate), new Date(selectedTournamentData.startDate));
      const matchingAgeCategory = resolveAgeCategory(age, selectedTournamentData.ageCategories ?? []);

      if (matchingAgeCategory) {
        setRegistrationForm(new ParticipantDto({
          ...registrationForm,
          ageCategoryInTournamentId: matchingAgeCategory.id
        }));
      } else {
        // Если не найдена подходящая категория, сбрасываем значение
        setRegistrationForm(new ParticipantDto({
          ...registrationForm,
          ageCategoryInTournamentId: undefined
        }));
      }
    }
  }, [showRegistrationModal, selectedTournamentData, currentUserData]);

  // Авто-загрузка превью при изменении категорий
  useEffect(() => {
    if (!showPreviewModal || !previewTournament || !previewAgeCategoryId || !previewWeightCategoryId) return;
    fetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewAgeCategoryId, previewWeightCategoryId, previewTournament?.id]);

  // Preview logic
  const openPreviewModal = async (tournament: Tournament) => {
    try {
      setPreviewError(null);
      setPreviewData(null);
      setPreviewTournament(null);
      setPreviewLoading(true);
      const data = await tournamentClient.getTournament(tournament.id);
      setPreviewTournament(data);
      const defaultAge = data.ageCategories && data.ageCategories.length > 0 ? data.ageCategories[0].id : undefined;
      const defaultWeight = data.weightCategories && data.weightCategories.length > 0 ? data.weightCategories[0].id : undefined;
      setPreviewAgeCategoryId(defaultAge);
      setPreviewWeightCategoryId(defaultWeight);
      setShowPreviewModal(true);
      // Если уже выбраны обе категории — сразу загружаем
      if (defaultAge && defaultWeight) {
        const resp = await tournamentClient.previewBracket(data.id, defaultWeight, defaultAge);
        setPreviewData(resp);
      }
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    } finally {
      setPreviewLoading(false);
    }
  };

  const fetchPreview = async () => {
    if (!previewTournament || !previewAgeCategoryId || !previewWeightCategoryId) {
      setPreviewError('Выберите весовую и возрастную категории');
      return;
    }
    try {
      setPreviewLoading(true);
      setPreviewError(null);
      const resp = await tournamentClient.previewBracket(
        previewTournament.id,
        previewWeightCategoryId,
        previewAgeCategoryId
      );
      setPreviewData(resp);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось загрузить предпросмотр');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApproveBracket = async (roundsPayload: { roundNumber: number; matches: { homeParticipantId?: number | null; awayParticipantId?: number | null }[] }[]) => {
    if (!previewTournament || !previewAgeCategoryId || !previewWeightCategoryId) return;
    try {
      setPreviewLoading(true);
      await tournamentClient.approveBracket(new ApproveBracketRequest({
        ageCategoryInTournamentId: previewAgeCategoryId,
        weightCategoryInTournamentId: previewWeightCategoryId,
        tournamentId: previewTournament.id,
        rounds: roundsPayload.map(rp => new RoundApproveDto({
          matches: rp.matches.map(m => new MatchApproveDto({
            homeParticipantId: m.homeParticipantId ?? undefined,
            awayParticipantId: m.awayParticipantId ?? undefined,
          })),
          roundNumber: rp.roundNumber
        })),
      }));
      setShowPreviewModal(false);
      setPreviewData(null);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось утвердить сетку');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Helper to (1) align next-round cards vertically and (2) compute connector paths
  const recalculateBracketLayout = React.useCallback(() => {
    if (!showPreviewModal || !previewData || !previewContainerRef.current) {
      setConnectorPaths([]);
      return;
    }
    const container = previewContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    const rounds = previewData.rounds ?? [];
    const hPad = 12;

    // 0) Reset transforms for stable measurement
    for (let r = 0; r < rounds.length; r++) {
      const matches = rounds[r].matches ?? [];
      for (let i = 0; i < matches.length; i++) {
        const el = document.getElementById(`match-r${r}-i${i}`) as HTMLElement | null;
        if (el) el.style.transform = '';
      }
    }

    // 1) Measure initial centers for all rounds (without transforms)
    const initialCenters: number[][] = [];
    for (let r = 0; r < rounds.length; r++) {
      const matches = rounds[r].matches ?? [];
      const centers: number[] = [];
      for (let i = 0; i < matches.length; i++) {
        const el = document.getElementById(`match-r${r}-i${i}`) as HTMLElement | null;
        if (!el) {
          centers.push(NaN);
          continue;
        }
        const rect = el.getBoundingClientRect();
        centers.push(rect.top + rect.height / 2 - containerRect.top);
      }
      initialCenters.push(centers);
    }

    // 2) Compute target centers: for r>0 each j is the midpoint of two previous matches (2j, 2j+1)
    const targetCenters: number[][] = initialCenters.map(arr => arr.slice());
    for (let r = 1; r < targetCenters.length; r++) {
      for (let j = 0; j < targetCenters[r].length; j++) {
        const p1 = targetCenters[r - 1][2 * j];
        const p2 = targetCenters[r - 1][2 * j + 1];
        if (Number.isFinite(p1) && Number.isFinite(p2)) {
          targetCenters[r][j] = (p1 as number + p2 as number) / 2;
        }
      }
    }

    // 3) Apply transforms to rounds r>=1 to move matches to their target centers
    for (let r = 1; r < rounds.length; r++) {
      const matches = rounds[r].matches ?? [];
      for (let i = 0; i < matches.length; i++) {
        const el = document.getElementById(`match-r${r}-i${i}`) as HTMLElement | null;
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const currentCenter = rect.top + rect.height / 2 - containerRect.top;
        const targetY = targetCenters[r][i];
        if (Number.isFinite(targetY)) {
          const delta = (targetY as number) - currentCenter;
          el.style.transform = `translateY(${delta}px)`;
        } else {
          el.style.transform = '';
        }
      }
    }

    // 4) Compute connectors after transforms are applied
    requestAnimationFrame(() => {
      const paths: string[] = [];
      for (let r = 0; r < rounds.length - 1; r++) {
        const nextMatches = rounds[r + 1].matches ?? [];
        for (let j = 0; j < nextMatches.length; j++) {
          const prevIndex1 = 2 * j;
          const prevIndex2 = 2 * j + 1;
          const prevEl1 = document.getElementById(`match-r${r}-i${prevIndex1}`);
          const prevEl2 = document.getElementById(`match-r${r}-i${prevIndex2}`);
          const nextEl = document.getElementById(`match-r${r + 1}-i${j}`);
          if (!nextEl) continue;
          const rectNext = (nextEl as HTMLElement).getBoundingClientRect();
          const xEnd = rectNext.left - containerRect.left;
          const yEnd = rectNext.top - containerRect.top + rectNext.height / 2;

          const has1 = !!prevEl1;
          const has2 = !!prevEl2;
          if (!has1 && !has2) continue;

          let xStart1 = 0, yStart1 = 0;
          let xStart2 = 0, yStart2 = 0;

          if (has1) {
            const rect1 = (prevEl1 as HTMLElement).getBoundingClientRect();
            xStart1 = rect1.right - containerRect.left;
            yStart1 = rect1.top - containerRect.top + rect1.height / 2;
          }
          if (has2) {
            const rect2 = (prevEl2 as HTMLElement).getBoundingClientRect();
            xStart2 = rect2.right - containerRect.left;
            yStart2 = rect2.top - containerRect.top + rect2.height / 2;
          }

          if (has1 && has2) {
            const xBase = Math.max(xStart1, xStart2) + hPad;
            const xJ = Math.min(xBase, xEnd - 12);
            const yMid = (yStart1 + yStart2) / 2;
            // horizontals from each prev to their group's vertical
            paths.push(`M ${xStart1} ${yStart1} H ${xJ}`);
            paths.push(`M ${xStart2} ${yStart2} H ${xJ}`);
            // group vertical only between this pair
            paths.push(`M ${xJ} ${Math.min(yStart1, yStart2)} V ${Math.max(yStart1, yStart2)}`);
            // mid of vertical to next match
            paths.push(`M ${xJ} ${yMid} H ${xEnd}`);
          } else {
            const xs = has1 ? xStart1 : xStart2;
            const ys = has1 ? yStart1 : yStart2;
            const xMid = Math.min(xs + hPad, xEnd - 12);
            paths.push(`M ${xs} ${ys} H ${xMid} V ${yEnd} H ${xEnd}`);
          }
        }
      }
      setConnectorPaths(paths);
    });
  }, [showPreviewModal, previewData]);

  // Compute connectors after render using layout effect to avoid flicker
  React.useLayoutEffect(() => {
    recalculateBracketLayout();
  }, [recalculateBracketLayout]);

  // Recompute connectors on window resize and container size changes
  useEffect(() => {
    if (!showPreviewModal) return;
    const handler = () => {
      recalculateBracketLayout();
    };
    window.addEventListener('resize', handler);

    const el = previewContainerRef.current;
    let ro: ResizeObserver | null = null;
    if (el && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => {
        recalculateBracketLayout();
      });
      ro.observe(el);
    }

    return () => {
      window.removeEventListener('resize', handler);
      if (ro && el) ro.unobserve(el);
    };
  }, [showPreviewModal, recalculateBracketLayout]);

  const renderBracketPreview = () => {
    if (!previewData) return null;
    return (
      <div style={{ position: 'relative', overflow: 'auto' }}>
        <BracketsPreview data={previewData} onApprove={previewLoading ? undefined : handleApproveBracket} />
      </div>
    );
  };

  return (
    <div className="tournaments-page">
      <div className="tournaments-page__header">
        <h2>Турниры</h2>
        <button onClick={() => navigate('/tournaments/create')} className="btn">Создать турнир</button>
      </div>

      {loading ? (
        <div>Загрузка данных...</div>
      ) : !tournaments || tournaments.length === 0 ? (
        <p>Турниров не найдено</p>
      ) : (
        <div className="tournament-list">
          <table>
            <thead>
              <tr>
                <th>Название</th>
                <th>Место проведения</th>
                <th>Дата начала</th>
                <th>Дата окончания</th>
                <th>Дата окончания регистрации</th>
                <th>Статус</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {tournaments.map((tournament) => (
                <tr key={tournament.id}>
                  <td>{tournament.name}</td>
                  <td>{tournament.location}</td>
                  <td>{new Date(tournament.startDate).toLocaleDateString()}</td>
                  <td>{new Date(tournament.endDate).toLocaleDateString()}</td>
                  <td>{new Date(tournament.registrationEndDate).toLocaleDateString()}</td>
                  <td>
                    {tournamentLifecycleLabel(getTournamentLifecycle(tournament))}
                  </td>
                  <td className="tournament-list__actions">
                    <button onClick={() => navigate(`/tournaments/${tournament.id}`)} className="btn btn--view">Просмотр</button>
                    {isAdmin() ? (
                      <>
                        <button onClick={() => {
                          navigate(`/tournaments/${tournament.id}/admin-register`);
                        }} className="btn">Регистрация</button>
                        <button onClick={() => openPreviewModal(tournament)} className="btn">Генерация</button>
                      </>
                    ) : user ? (
                      isUserRegistered(tournament.id!) ? (
                        <span className="tournament-list__registered-status">Вы зарегистрированы</span>
                      ) : (
                        <button
                          onClick={() => handleRegisterClick(tournament)}
                          className="btn btn--register"
                          disabled={tournament.isStarted || new Date() > new Date(tournament.registrationEndDate)}
                        >
                          Зарегистрироваться на турнир
                        </button>
                      )
                    ) : (
                      <button onClick={() => openPreviewModal(tournament)} className="btn">Генерация</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Модальное окно регистрации */}
      {showRegistrationModal && selectedTournament && (
        <div className="modal__overlay">
          <div className="modal__content" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>Регистрация на турнир "{selectedTournament.name}"</h3>
              <button className="modal__close-btn" onClick={() => setShowRegistrationModal(false)}>&times;</button>
            </div>
            <div className="modal__body">
              <div className="form__group">
                <label>Участник:</label>
                <input
                  type="text"
                  value={`${user?.lastName} ${user?.firstName}${currentUserData && selectedTournamentData ? ` (${calculateAge(new Date(currentUserData.birthDate), new Date(selectedTournamentData.startDate))} лет)` : ''}`}
                  disabled
                />
              </div>
              <div className="form__group">
                <label>Вес (кг):</label>
                <input
                  type="number"
                  min="0"
                  max="200"
                  step="0.1"
                  value={registrationForm.weight || ''}
                  onChange={(e) => setRegistrationForm(new ParticipantDto({
                    ...registrationForm,
                    weight: parseFloat(e.target.value)
                  }))}
                  required
                />
              </div>
              <div className="form__group">
                <label>Спортивная школа:</label>
                <input
                  type="text"
                  value={registrationForm.organization}
                  onChange={(e) => setRegistrationForm(new ParticipantDto({
                    ...registrationForm,
                    organization: e.target.value
                  }))}
                  required
                />
              </div>
              <div className="form__group">
                <label>Тренер (необязательно):</label>
                <input
                  type="text"
                  value={registrationForm.coachId || ''}
                  onChange={(e) => setRegistrationForm(new ParticipantDto({
                    ...registrationForm,
                    coachId: e.target.value ? parseInt(e.target.value) : undefined
                  }))}
                />
              </div>
              <div className="form__group">
                <label>Разряд (необязательно):</label>
                <input
                  type="text"
                  value={registrationForm.rank || ''}
                  onChange={(e) => setRegistrationForm(new ParticipantDto({
                    ...registrationForm,
                    rank: e.target.value
                  }))}
                />
              </div>
              <div className="form__group">
                <label>Возрастная категория:</label>
                <select
                  value={registrationForm.ageCategoryInTournamentId || ''}
                  onChange={(e) => setRegistrationForm(new ParticipantDto({
                    ...registrationForm,
                    ageCategoryInTournamentId: e.target.value ? parseInt(e.target.value) : undefined
                  }))}
                >
                  <option value="">{getAgeCategoryText(registrationForm.ageCategoryInTournamentId)}</option>
                  {selectedTournamentData?.ageCategories
                    ?.slice()
                    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                    .map(ac => (
                      <option key={ac.id} value={ac.id}>
                        {formatAgeCategoryLabel(ac, selectedTournamentData.ageCategories ?? [])}
                      </option>
                    ))}
                </select>
              </div>
              <div className="form__group">
                <label>Весовая категория:</label>
                <select
                  value={selectedWeightCategoryId || ''}
                  onChange={(e) => setSelectedWeightCategoryId(e.target.value ? parseInt(e.target.value) : undefined)}
                >
                  <option value="">{getWeightCategoryText(selectedWeightCategoryId)}</option>
                  {selectedTournamentData?.weightCategories
                    ?.slice()
                    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                    .map(wc => (
                      <option key={wc.id} value={wc.id}>
                        {formatWeightCategoryLabel(wc, selectedTournamentData.weightCategories ?? [])}
                      </option>
                    ))}
                </select>
              </div>
              <div className="modal__actions">
                <button onClick={handleRegistrationSubmit} className="btn btn--register">
                  Зарегистрироваться
                </button>
                <button onClick={() => setShowRegistrationModal(false)} className="btn btn--back">
                  Отменить
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно предпросмотра сетки */}
      {showPreviewModal && previewTournament && (
        <div className="modal__overlay" onClick={() => setShowPreviewModal(false)}>
          <div
            className="modal__content preview-bracket"
            style={(previewData?.rounds?.length ?? 0) <= 4 ? { maxWidth: '1000px' } : undefined}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal__header">
              <h3>Предпросмотр сетки: "{previewTournament.name}"</h3>
              <button className="modal__close-btn" onClick={() => setShowPreviewModal(false)}>&times;</button>
            </div>
            <div className="modal__body">
              {/* Выбор категорий */}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                <div className="form__group" style={{ flex: 1, minWidth: 200 }}>
                  <label>Возрастная категория:</label>
                  <select
                    value={previewAgeCategoryId || ''}
                    onChange={(e) => setPreviewAgeCategoryId(e.target.value ? parseInt(e.target.value) : undefined)}
                  >
                    <option value="">Выберите возрастную категорию</option>
                    {previewTournament.ageCategories
                      ?.slice()
                      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                      .map(ac => (
                        <option key={ac.id} value={ac.id}>
                          {formatAgeCategoryLabel(ac, previewTournament.ageCategories ?? [])}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="form__group" style={{ flex: 1, minWidth: 200 }}>
                  <label>Весовая категория:</label>
                  <select
                    value={previewWeightCategoryId || ''}
                    onChange={(e) => setPreviewWeightCategoryId(e.target.value ? parseInt(e.target.value) : undefined)}
                  >
                    <option value="">Выберите весовую категорию</option>
                    {previewTournament.weightCategories
                      ?.slice()
                      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                      .map(wc => (
                        <option key={wc.id} value={wc.id}>
                          {formatWeightCategoryLabel(wc, previewTournament.weightCategories ?? [])}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {previewError && <div className="error-message" style={{ marginBottom: 12 }}>{previewError}</div>}

              {/* Превью сетки */}
              {previewLoading && !previewData && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Загрузка сетки...</div>
              )}

              {!previewLoading && !previewData && previewAgeCategoryId && previewWeightCategoryId && (
                <div style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>Нет участников для выбранных категорий</div>
              )}

              {previewData && (
                <div>
                  <div style={{ marginBottom: 8, color: '#374151', fontWeight: 500 }}>
                    {previewData.weightCategory} · {previewData.ageCategory}
                    {previewLoading && <span style={{ marginLeft: 8, color: '#6b7280', fontWeight: 400, fontSize: '0.9rem' }}>обновление...</span>}
                  </div>
                  {renderBracketPreview()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
